/**
 * Cloudflare Turnstile's site key, resolved once for every app.
 *
 * ## Why this module exists
 *
 * `VITE_TURNSTILE_SITE_KEY` is in all five `.env.example` files and belongs in all five
 * Cloudflare Pages projects — and until 2026-09-21 exactly one app read it. `app_partners`
 * had the resolver in its own `data/links.ts`, so the Foundation's contact form, the
 * garage's, the resort's enquiry and Steward's sign-in each had the variable configured and
 * no code that looked at it. Configuring a key would have protected one form out of nine.
 *
 * Ground rule 5: one definition. This is it, beside `apiOrigin.ts` and for the same reason
 * — it is a value more than one app reads, with no DOM, no React and no bundler global.
 *
 * ## Why it takes an env record
 *
 * The same reason `resolveSiteLinks` and `resolveApiRoot` do: `import.meta` is a
 * bundler-specific global and `app_design` has to stay importable from React Native when
 * `app_mobile` arrives. The caller passes its own environment.
 *
 *     export const TURNSTILE_SITE_KEY = resolveTurnstileSiteKey(import.meta.env)
 *
 * ## Empty is a valid value, and it is the value today
 *
 * There is no Turnstile account yet. An unset key renders **no widget** and leaves every
 * form working exactly as it does now — `Turnstile` returns `null` for it — and the server's
 * half matches: `app_api` treats a missing token as a failure **only when it has a secret
 * configured**. So an unset key cannot accidentally refuse everybody, and setting both
 * halves is the whole change the day there is an account.
 *
 * That symmetry is the thing to preserve. A front end that always sends a token and a server
 * that never checks one is theatre; a server that always demands one and a front end that
 * cannot produce one is an outage. Both ends read their own half of the same pair.
 */

/** The variable that configures it. Public, like every other `VITE_` value. */
export const TURNSTILE_SITE_KEY_ENV_VAR = 'VITE_TURNSTILE_SITE_KEY'

/** An env value only counts if it is a non-empty string — an unset Vite var is `''`. */
const configured = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== ''

/**
 * The site key, or `undefined` when none is set.
 *
 * `undefined` rather than `''` on purpose: `Turnstile`'s `siteKey` is optional, and an
 * empty string passed where a key is expected is the shape that gets rendered into a
 * widget with no key and hangs on "loading".
 */
export function resolveTurnstileSiteKey(env: Record<string, unknown> | undefined): string | undefined {
  const value = env?.[TURNSTILE_SITE_KEY_ENV_VAR]
  return configured(value) ? value.trim() : undefined
}
